"use client";

import { Suspense, useState, useCallback, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/lib/store";
import { useChatRevealStore, surfaceToTab } from "@/lib/stores/chatReveal";
import BottomNav from "@/components/BottomNav";
import DesktopSidebar from "@/components/DesktopSidebar";
import TodayScreen from "@/components/today/TodayScreen";
import PlanScreen from "@/components/plan/PlanScreen";
import DecideScreen from "@/components/DecideScreen";
import LifeScreen from "@/components/LifeScreen";
import CrossSurfaceConfirmationBanner from "@/components/CrossSurfaceConfirmationBanner";
import Onboarding from "@/components/Onboarding";
import {
  OverlayManagerProvider,
  useOverlayManager,
} from "@/components/overlays/OverlayManager";

const FIRST_SESSION_TODAY_KEY = "de_today_first_session_pending";
// Once-per-device gate for the quick-tour video menu auto-open. Set when the
// menu has auto-opened once; the (?) button replays it any time after.
const SEEN_VIDEO_TOUR_KEY = "de_seen_video_tour";

type DecideView = "decisions" | "library" | "goals" | "balance";

const VALID_TABS = new Set(["present", "schedule", "decide", "life"]);
const VALID_DECIDE_VIEWS = new Set<DecideView>(["decisions", "library", "goals", "balance"]);

function PageLoadingFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="min-h-app-minus-mobile-nav flex items-center justify-center px-4"
    >
      <div className="text-center max-w-xs">
        <div
          aria-hidden="true"
          className="w-8 h-8 border-2 border-forest border-t-transparent rounded-full animate-spin motion-reduce:animate-none mx-auto mb-4"
        />
        <p className="text-sm text-muted">
          Waking up <span translate="no">DecisionEase</span>…
        </p>
        <p className="text-xs text-muted mt-1 opacity-70">
          First visit may take up to a minute
        </p>
      </div>
    </div>
  );
}

export default function HomePage() {
  // Suspense wraps HomePageInner because useSearchParams() requires a
  // Suspense boundary during the prerender pass; without this Vercel's
  // build fails with "missing-suspense-with-csr-bailout".
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <HomePageInner />
    </Suspense>
  );
}

function HomePageInner() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  // Initialise tab + sub-tab from URL so refresh and bookmarks preserve position.
  const [activeTab, setActiveTab] = useState<string>(() => {
    const t = searchParams?.get("tab");
    return t && VALID_TABS.has(t) ? t : "present";
  });
  const [decideView, setDecideView] = useState<DecideView>(() => {
    const v = searchParams?.get("view") as DecideView | null;
    return v && VALID_DECIDE_VIEWS.has(v) ? v : "decisions";
  });
  const [tabVisitCount, setTabVisitCount] = useState(0);
  const [scheduleAutoOpenPlanToken, setScheduleAutoOpenPlanToken] = useState(0);
  const [onboardingDone, setOnboardingDone] = useState<boolean | null>(null);
  const [firstSessionPending, setFirstSessionPending] = useState<boolean | null>(null);

  useEffect(() => {
    if (!loading && !user) {
      router.replace("/sign-in");
    }
    if (!loading && user) {
      setOnboardingDone(user.onboarding_complete);
      setFirstSessionPending(localStorage.getItem(FIRST_SESSION_TODAY_KEY) === "true");
    }
  }, [loading, user, router]);

  // Reflect activeTab + decideView to URL search params so a refresh keeps
  // the user on the same tab. Uses router.replace (not push) so back-button
  // history isn't polluted with every internal tab change.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams();
    if (activeTab !== "present") params.set("tab", activeTab);
    if (activeTab === "decide" && decideView !== "decisions") {
      params.set("view", decideView);
    }
    const qs = params.toString();
    const target = qs ? `/?${qs}` : "/";
    const current = window.location.pathname + window.location.search;
    if (current !== target) {
      router.replace(target, { scroll: false });
    }
  }, [activeTab, decideView, router]);

  const handleOnboardingComplete = useCallback(() => {
    localStorage.setItem(FIRST_SESSION_TODAY_KEY, "true");
    setOnboardingDone(true);
    setFirstSessionPending(true);
    setActiveTab("present");
  }, []);

  const handleFirstSessionComplete = useCallback(() => {
    localStorage.removeItem(FIRST_SESSION_TODAY_KEY);
    setFirstSessionPending(false);
  }, []);

  // U24 — Chat reveal navigation. ChatScreen sets `pending` after a
  // successful action commit returns a typed `ActionReveal`; we route
  // to the matching tab/sub-tab and consume the entry so each reveal
  // fires exactly once. The chat sheet is already closed by the
  // banner click handler before this runs.
  const pendingReveal = useChatRevealStore((s) => s.pending);
  const consumeReveal = useChatRevealStore((s) => s.consume);
  useEffect(() => {
    if (!pendingReveal) return;
    const route = surfaceToTab(pendingReveal.surface);
    setActiveTab(route.activeTab);
    if (route.decideView) {
      setDecideView(route.decideView);
    }
    setTabVisitCount((c) => c + 1);
    consumeReveal();
  }, [pendingReveal, consumeReveal]);

  if (loading) {
    return <PageLoadingFallback />;
  }

  if (!user) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="min-h-app-minus-mobile-nav flex items-center justify-center px-4"
      >
        <div className="text-center">
          <div
            aria-hidden="true"
            className="w-8 h-8 border-2 border-forest border-t-transparent rounded-full animate-spin motion-reduce:animate-none mx-auto mb-4"
          />
          <p className="text-sm text-muted">Redirecting to sign in…</p>
        </div>
      </div>
    );
  }

  if (onboardingDone === false) {
    return (
      <Onboarding
        userName={user.display_name || "Friend"}
        onComplete={handleOnboardingComplete}
      />
    );
  }

  if (onboardingDone && firstSessionPending === null) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="min-h-app-minus-mobile-nav flex items-center justify-center"
      >
        <span className="sr-only">Loading…</span>
        <div
          aria-hidden="true"
          className="w-8 h-8 border-2 border-forest border-t-transparent rounded-full animate-spin motion-reduce:animate-none"
        />
      </div>
    );
  }

  const navActiveTab = activeTab;
  const contentWidthClass =
    activeTab === "schedule" ||
    activeTab === "life" ||
    (activeTab === "decide" && (decideView === "goals" || decideView === "balance"))
      ? "lg:max-w-5xl"
      : "lg:max-w-3xl";

  // Effective surface for chat grounding. Decide subtabs (goals, library,
  // decisions, balance) are first-class surfaces so chat can scope itself
  // correctly instead of always saying "Grounded in Review" (UAT 2026-05-06
  // foundational fix). Top-level tabs map to canonical surface names.
  const effectiveSurface =
    activeTab === "decide"
      ? decideView
      : activeTab === "present"
      ? "today"
      : activeTab === "schedule"
      ? "plan"
      : activeTab === "life"
      ? "reflect"
      : activeTab;

  return (
    <OverlayManagerProvider routeKey={activeTab} screenContext={effectiveSurface}>
      <AuthenticatedShell
        activeTab={activeTab}
        decideView={decideView}
        tabVisitCount={tabVisitCount}
        scheduleAutoOpenPlanToken={scheduleAutoOpenPlanToken}
        firstSessionPending={Boolean(firstSessionPending)}
        navActiveTab={navActiveTab}
        contentWidthClass={contentWidthClass}
        onNavigateState={(tab, initialMessage) => {
          if (tab !== "schedule-plan") {
            setScheduleAutoOpenPlanToken(0);
          }
          if (tab === "open-chat") {
            return { handled: false, initialMessage };
          }
          if (tab === "goals") {
            setActiveTab("decide");
            setDecideView("goals");
            setTabVisitCount((c) => c + 1);
            return { handled: true };
          }
          if (tab === "balance") {
            setActiveTab("decide");
            setDecideView("balance");
            setTabVisitCount((c) => c + 1);
            return { handled: true };
          }
          if (tab === "library") {
            setActiveTab("decide");
            setDecideView("library");
            setTabVisitCount((c) => c + 1);
            return { handled: true };
          }
          if (tab === "schedule-plan") {
            setActiveTab("schedule");
            setScheduleAutoOpenPlanToken((count) => count + 1);
            setTabVisitCount((c) => c + 1);
            return { handled: true };
          }
          if (tab === "decide") {
            setDecideView("decisions");
          }
          setActiveTab(tab);
          setTabVisitCount((c) => c + 1);
          return { handled: true };
        }}
        onTabStateChange={(tab) => {
          if (tab === "schedule") {
            setScheduleAutoOpenPlanToken(0);
          }
          if (tab === "decide") {
            setDecideView("decisions");
          }
          setActiveTab(tab);
          setTabVisitCount((c) => c + 1);
        }}
        onDecideViewChange={setDecideView}
        onFirstSessionComplete={handleFirstSessionComplete}
      />
    </OverlayManagerProvider>
  );
}

interface ShellProps {
  activeTab: string;
  decideView: "decisions" | "library" | "goals" | "balance";
  tabVisitCount: number;
  scheduleAutoOpenPlanToken: number;
  firstSessionPending: boolean;
  navActiveTab: string;
  contentWidthClass: string;
  onNavigateState: (
    tab: string,
    initialMessage?: string
  ) => { handled: boolean; initialMessage?: string };
  onTabStateChange: (tab: string) => void;
  /** Lift DecideScreen subtab changes back into HomePageInner so URL
   *  params + OverlayManager screenContext stay in sync with the
   *  visible pill (WIP-1 Phase 2D fix). */
  onDecideViewChange: (view: "decisions" | "library" | "goals" | "balance") => void;
  onFirstSessionComplete: () => void;
}

function AuthenticatedShell({
  activeTab,
  decideView,
  tabVisitCount,
  scheduleAutoOpenPlanToken,
  firstSessionPending,
  navActiveTab,
  contentWidthClass,
  onNavigateState,
  onTabStateChange,
  onDecideViewChange,
  onFirstSessionComplete,
}: ShellProps) {
  const overlays = useOverlayManager();
  const { openHelp } = overlays;
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem("de_sidebar_collapsed");
    if (stored === "true") setSidebarCollapsed(true);
  }, []);

  // First arrival in the app (right after onboarding, or an existing device
  // that has never seen the tours): auto-open the quick-tour video menu once
  // per device. The flag is stamped inside the timeout so StrictMode's
  // mount→unmount→mount in dev still fires it exactly once.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(SEEN_VIDEO_TOUR_KEY) === "true") return;
    const timer = window.setTimeout(() => {
      window.localStorage.setItem(SEEN_VIDEO_TOUR_KEY, "true");
      openHelp();
    }, 800);
    return () => window.clearTimeout(timer);
  }, [openHelp]);

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((current) => {
      const next = !current;
      if (typeof window !== "undefined") {
        window.localStorage.setItem("de_sidebar_collapsed", next ? "true" : "false");
      }
      return next;
    });
  }, []);

  const handleNavigate = useCallback(
    (tab: string, initialMessage?: string) => {
      overlays.closeAll();
      const result = onNavigateState(tab, initialMessage);
      if (!result.handled) {
        overlays.openChat(result.initialMessage ?? null);
      }
    },
    [onNavigateState, overlays]
  );

  const handleTabChange = useCallback(
    (tab: string) => {
      overlays.closeAll();
      onTabStateChange(tab);
    },
    [onTabStateChange, overlays]
  );

  return (
    <div className="min-h-app flex min-w-0">
      <DesktopSidebar
        activeTab={navActiveTab}
        onTabChange={handleTabChange}
        onChatOpen={() => overlays.openChat()}
        onSettingsOpen={overlays.openSettings}
        onHelpOpen={overlays.openHelp}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={toggleSidebarCollapsed}
      />

      <div
        className={`min-h-app min-w-0 flex-1 flex flex-col transition-[margin] duration-200 ease-out ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="lg:hidden flex items-center justify-between px-4 pt-3 pb-1">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-forest flex items-center justify-center">
              <span translate="no" className="text-white text-[9px] font-bold">DE</span>
            </div>
            <span translate="no" className="text-sm font-semibold text-forest-med tracking-wide">DecisionEase</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={overlays.openHelp}
              data-testid="mobile-help-btn"
              aria-label="Watch quick tours"
              className="flex h-11 w-11 items-center justify-center rounded-full text-muted hover:text-text hover:bg-secondary/50 active:bg-secondary/70 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-med focus-visible:ring-offset-2"
            >
              <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
                <path d="M12 17h.01" />
              </svg>
            </button>
            <button
              onClick={overlays.openSettings}
              data-testid="mobile-settings-btn"
              aria-label="Open settings"
              className="flex h-11 w-11 items-center justify-center rounded-lg text-muted hover:text-text hover:bg-secondary/50 active:bg-secondary/70 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-med focus-visible:ring-offset-2"
            >
              <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
              </svg>
            </button>
          </div>
        </div>

        {/*
          Bottom padding has to clear the fixed BottomNav (height ~72px
          rendered + iOS safe-area-inset-bottom) so scroll content
          doesn't sit behind it. The old pb-16 (64px) was too small —
          paginator arrows, weighting pills, AI-notes button, etc. on
          deeper screens got eaten by the nav strip. The Mobile button
          audit (frontend/e2e/mobile-button-audit.spec.ts) regression-
          guards this.
        */}
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden pb-[calc(env(safe-area-inset-bottom)+7rem)] lg:pb-0">
          <div className={`w-full min-w-0 lg:mx-auto ${contentWidthClass}`}>
            <CrossSurfaceConfirmationBanner onNavigate={handleNavigate} />
            {activeTab === "present" && (
              <TodayScreen
                key={`present-${tabVisitCount}`}
                onNavigate={handleNavigate}
                isFirstSessionPending={firstSessionPending}
                onFirstSessionComplete={onFirstSessionComplete}
              />
            )}
            {activeTab === "schedule" && (
              <PlanScreen
                key={`schedule-${tabVisitCount}-${scheduleAutoOpenPlanToken}`}
                onNavigate={handleNavigate}
                autoOpenPlanToken={scheduleAutoOpenPlanToken}
              />
            )}
            {activeTab === "decide" && (
              <DecideScreen
                key={`decide-${tabVisitCount}-${decideView}`}
                onNavigate={handleNavigate}
                initialView={decideView}
                onViewChange={onDecideViewChange}
              />
            )}
            {activeTab === "life" && (
              <LifeScreen key={`life-${tabVisitCount}`} onNavigate={handleNavigate} />
            )}
          </div>
        </div>

        <div className="lg:hidden">
          <BottomNav activeTab={navActiveTab} onTabChange={handleTabChange} />
        </div>
      </div>
    </div>
  );
}
